import { forwardRef, useCallback, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Slider,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Check, CenterFocusStrong, ZoomIn, ZoomOut } from "@mui/icons-material";
import { VirtuosoGrid } from "react-virtuoso";
import { cdnDatasetImageUrl, type DatasetImage } from "../api";
import t from "../translations";
import { useDatasetDetail } from "./DatasetDetailContext";
import {
  DEFAULT_IMAGE_SIZE,
  MAX_IMAGE_SIZE,
  MIN_IMAGE_SIZE,
  formatCropLabel,
} from "./cropImage";
import { useCropWindow, type Crop } from "./useCropWindow";

// Forwarded ref required — virtuoso measures the list container.
const GridList = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ style, children, ...rest }, ref) => (
  <div
    ref={ref}
    {...rest}
    style={{
      ...style,
      display: "flex",
      flexWrap: "wrap",
      alignContent: "flex-start",
    }}
  >
    {children}
  </div>
));
GridList.displayName = "CropGridList";

export interface CropGridProps {
  // Source images whose crops to fetch — typically the filtered image list
  // for an all-dataset view or `[selectedImage]` for a per-image strip.
  sourceImages: DatasetImage[];
  // Prefix for data-testid values so tests can target each instance.
  testIdPrefix: string;
  // Controls the empty-state message: "image" distinguishes mapped vs unmapped;
  // "dataset" always shows the dataset-level message.
  context: "image" | "dataset";
  // True while the upstream source list is still loading — suppresses the
  // empty state so the user doesn't see "no crops" flash before fetch starts.
  sourcesLoading?: boolean;
  // When set, crops whose imageId matches get a primary-coloured outline.
  activeImageId?: number;
  // When set, clicking a crop calls this with its image id + project id.
  onCropClick?: (imageId: number, projectId: number) => void;
  // Builds the tooltip text for one crop. Defaults to just the crop label.
  formatTooltip?: (crop: Crop, defaultLabel: string) => string;
  // When true, cells whose imageId is in the dataset's seen-this-session set
  // render a small check badge in the bottom-right corner.
  showSeenBadge?: boolean;
}

export default function CropGrid({
  sourceImages,
  testIdPrefix,
  context,
  sourcesLoading = false,
  activeImageId = -1,
  onCropClick,
  formatTooltip,
  showSeenBadge = false,
}: CropGridProps) {
  const { datasetId, seenImageIds } = useDatasetDetail();
  const [imageSize, setImageSize] = useState(DEFAULT_IMAGE_SIZE);

  const {
    crops,
    fetching,
    forwardExhausted,
    seeking,
    imageMapped,
    gridRef,
    goToActive,
    onEndReached,
    onStartReached,
    onRangeChanged,
  } = useCropWindow({ sourceImages, activeImageId });

  // Loading covers the one-frame gap between reset and the priming fetch
  // (sources present but not yet fetching) as well as actual fetch/load.
  const showInitialLoading =
    crops.length === 0 &&
    (sourcesLoading ||
      fetching ||
      (sourceImages.length > 0 && !forwardExhausted));
  // Empty when there's nothing to show and nothing more coming — either
  // we've fetched everything or the source set is genuinely empty.
  const showEmptyState =
    crops.length === 0 &&
    !sourcesLoading &&
    !fetching &&
    (forwardExhausted || sourceImages.length === 0);

  let emptyMessage: string;
  if (context === "dataset") {
    emptyMessage = t.NoDatasetCropsFound;
  } else if (imageMapped === false) {
    emptyMessage = t.ImageCropsCouldntBeFound;
  } else {
    emptyMessage = t.MappedImageHasNoCrops;
  }

  const itemContent = useCallback(
    (_: number, crop: Crop) => {
      const label = formatCropLabel(crop);
      const tooltip = formatTooltip ? formatTooltip(crop, label) : label;
      const active = crop.imageId === activeImageId;
      const seen = showSeenBadge && seenImageIds.has(crop.imageId);
      // crop.path is the only guaranteed unique-per-image suffix.
      const cropTestId = crop.path || `idx${crop.cropIndex ?? 0}`;
      const handleClick = onCropClick
        ? () => onCropClick(crop.imageId, crop.projectId)
        : undefined;
      const handleKeyDown = handleClick
        ? (e: React.KeyboardEvent) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleClick();
            }
          }
        : undefined;
      return (
        <Tooltip title={tooltip} placement="bottom" arrow disableInteractive>
          <Box
            role={handleClick ? "button" : undefined}
            tabIndex={handleClick ? 0 : undefined}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            sx={{
              width: imageSize,
              p: "5px",
              position: "relative",
              cursor: handleClick ? "pointer" : "default",
            }}
            data-testid={`${testIdPrefix}.cropCell.${crop.imageId}.${cropTestId}`}
            data-active={active ? "" : undefined}
          >
            <Box
              component="img"
              src={cdnDatasetImageUrl(crop.region, datasetId, crop.path)}
              alt={label}
              draggable={false}
              loading="lazy"
              sx={{
                width: "100%",
                // Fixed square box so a cell's height is known before its image
                // loads — VirtuosoGrid needs uniform cells or the scroll
                // position drifts as lazy images stream in.
                aspectRatio: "1 / 1",
                objectFit: "contain",
                backgroundColor: "rgba(0,0,0,0.25)",
                display: "block",
                border: active ? "5px solid" : "1px solid",
                borderColor: active ? "primary.main" : "rgba(255,255,255,0.5)",
                borderRadius: "4px",
                boxShadow: active ? "0 0 0 3px rgba(144,202,249,0.35)" : "none",
              }}
              data-testid={`${testIdPrefix}.crop.${crop.imageId}.${cropTestId}`}
            />
            {seen && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 8,
                  right: 8,
                  width: 18,
                  height: 18,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "50%",
                  bgcolor: "rgba(0,0,0,0.65)",
                  pointerEvents: "none",
                }}
                data-testid={`${testIdPrefix}.seenBadge.${crop.imageId}.${cropTestId}`}
                aria-label={t.ImageSeenThisSession}
              >
                <Check sx={{ fontSize: 14, color: "common.white" }} />
              </Box>
            )}
          </Box>
        </Tooltip>
      );
    },
    [
      activeImageId,
      datasetId,
      formatTooltip,
      imageSize,
      onCropClick,
      seenImageIds,
      showSeenBadge,
      testIdPrefix,
    ],
  );

  const Footer = useCallback(
    () =>
      fetching ? (
        <Stack
          sx={{ alignItems: "center", justifyContent: "center", py: 2 }}
          data-testid={`${testIdPrefix}.loadingMore`}
        >
          <CircularProgress size={20} />
          <Typography variant="caption" sx={{ color: "text.secondary", mt: 1 }}>
            {t.AllCropsLoading}
          </Typography>
        </Stack>
      ) : null,
    [fetching, testIdPrefix],
  );

  const components = useMemo(() => ({ List: GridList, Footer }), [Footer]);

  return (
    <Stack
      sx={{
        width: "100%",
        height: "100%",
        bgcolor: "background.paper",
        overflow: "hidden",
      }}
      data-testid={`${testIdPrefix}.container`}
    >
      <Stack
        sx={{
          flexDirection: "row",
          alignItems: "center",
          gap: 1,
          px: 1.25,
          py: 0.5,
          flexShrink: 0,
          borderBottom: 1,
          borderColor: "divider",
        }}
        data-testid={`${testIdPrefix}.toolbar`}
      >
        <ZoomOut fontSize="small" sx={{ color: "rgba(255,255,255,0.6)" }} />
        <Slider
          min={MIN_IMAGE_SIZE}
          max={MAX_IMAGE_SIZE}
          value={imageSize}
          onChange={(_, v) => setImageSize(v as number)}
          size="small"
          sx={{ color: "rgba(255,255,255,0.5)", mx: 1, flex: 1 }}
        />
        <ZoomIn fontSize="small" sx={{ color: "rgba(255,255,255,0.6)" }} />
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", whiteSpace: "nowrap", mx: 1 }}
          data-testid={`${testIdPrefix}.count`}
        >
          {t.CropCount(crops.length)}
        </Typography>
        {activeImageId >= 0 && (
          <Tooltip title={t.GoToCurrentCrop} arrow>
            <span>
              <IconButton
                size="small"
                onClick={goToActive}
                disabled={seeking}
                aria-label={t.GoToCurrentCrop}
                data-testid={`${testIdPrefix}.goToCurrent`}
                sx={{ color: "rgba(255,255,255,0.7)" }}
              >
                {seeking ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <CenterFocusStrong fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Stack>
      <Box sx={{ flex: 1, minHeight: 0 }}>
        {showInitialLoading ? (
          <Stack
            sx={{ alignItems: "center", justifyContent: "center", py: 4 }}
            data-testid={`${testIdPrefix}.initialLoading`}
          >
            <CircularProgress size={24} />
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", mt: 1 }}
            >
              {t.AllCropsLoading}
            </Typography>
          </Stack>
        ) : showEmptyState ? (
          <Typography
            sx={{ textAlign: "center", color: "rgba(255,255,255,0.5)", py: 4 }}
            data-testid={`${testIdPrefix}.empty`}
          >
            {emptyMessage}
          </Typography>
        ) : (
          <VirtuosoGrid
            ref={gridRef}
            style={{ width: "100%", height: "100%" }}
            data={crops}
            computeItemKey={(_, crop) => crop.key}
            endReached={onEndReached}
            startReached={onStartReached}
            rangeChanged={onRangeChanged}
            increaseViewportBy={400}
            listClassName="cropGrid-list"
            itemContent={itemContent}
            components={components}
          />
        )}
      </Box>
    </Stack>
  );
}
